import React from 'react';
import { useWeb } from '../../context/WebContext';
import { FaEnvelope, FaList, FaImages, FaMapMarkerAlt, FaCheck, FaTrashAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';

export default function Dashboard() {
  const { enquiries, services, gallery, locations, markEnquiryRead, deleteEnquiry } = useWeb();

  // Metrics
  const totalEnquiries = enquiries.length;
  const unreadEnquiriesCount = enquiries.filter(e => e.status === 'unread').length;
  const totalServices = services.length;
  const totalGallery = gallery.length;
  const activeLocations = locations.filter(l => l.active).length;

  const recentEnquiries = enquiries.slice(0, 5);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">Dashboard Overview</h1>
        <p className="text-slate-500 text-xs sm:text-sm mt-1">Here is a quick snapshot of your website lead generation and content count.</p>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Enquiry Metric */}
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 flex items-center justify-between shadow-sm">
          <div className="space-y-1">
            <span className="text-slate-400 text-xs font-bold uppercase tracking-wider">Unread Enquiries</span>
            <h3 className="text-3xl font-black text-slate-900">
              {unreadEnquiriesCount} <span className="text-xs text-slate-400 font-normal">/ {totalEnquiries} total</span>
            </h3>
          </div>
          <div className="bg-orange-50 text-orange-600 p-4 rounded-xl text-xl">
            <FaEnvelope />
          </div>
        </div>

        {/* Services Metric */}
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 flex items-center justify-between shadow-sm">
          <div className="space-y-1">
            <span className="text-slate-400 text-xs font-bold uppercase tracking-wider">Active Services</span>
            <h3 className="text-3xl font-black text-slate-900">{totalServices}</h3>
          </div>
          <div className="bg-blue-50 text-blue-600 p-4 rounded-xl text-xl">
            <FaList />
          </div>
        </div>

        {/* Gallery Metric */}
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 flex items-center justify-between shadow-sm">
          <div className="space-y-1">
            <span className="text-slate-400 text-xs font-bold uppercase tracking-wider">Gallery Works</span>
            <h3 className="text-3xl font-black text-slate-900">{totalGallery}</h3>
          </div>
          <div className="bg-purple-50 text-purple-600 p-4 rounded-xl text-xl">
            <FaImages />
          </div>
        </div>

        {/* Locations Metric */}
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 flex items-center justify-between shadow-sm">
          <div className="space-y-1">
            <span className="text-slate-400 text-xs font-bold uppercase tracking-wider">Locations</span>
            <h3 className="text-3xl font-black text-slate-900">{activeLocations}</h3>
          </div>
          <div className="bg-green-50 text-green-600 p-4 rounded-xl text-xl">
            <FaMapMarkerAlt />
          </div>
        </div>
      </div>

      {/* Recent Enquiries Box */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
          <h3 className="font-bold text-slate-950 text-sm sm:text-base">Recent Enquiries</h3>
          <Link to="/admin/enquiries" className="text-xs font-bold text-orange-600 hover:text-orange-700 transition">
            View All Enquiries →
          </Link>
        </div>

        <div className="overflow-x-auto">
          {recentEnquiries.length > 0 ? (
            <table className="w-full text-left text-xs sm:text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase font-bold text-[10px] tracking-wider border-b border-slate-100">
                <tr>
                  <th className="px-6 py-4">Customer</th>
                  <th className="px-6 py-4">Service</th>
                  <th className="px-6 py-4">City</th>
                  <th className="px-6 py-4">Date</th>
                  <th className="px-6 py-4 text-center">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {recentEnquiries.map((enq) => (
                  <tr key={enq.id} className="hover:bg-slate-50/40">
                    <td className="px-6 py-4 font-medium text-slate-900">
                      <div>{enq.name}</div>
                      <div className="text-[10px] text-slate-400 font-normal">{enq.phone}</div>
                    </td>
                    <td className="px-6 py-4 text-slate-600">{enq.service}</td>
                    <td className="px-6 py-4 text-slate-600">{enq.location}</td>
                    <td className="px-6 py-4 text-slate-400 text-xs">
                      {new Date(enq.date).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-extrabold ${
                        enq.status === 'unread' 
                          ? 'bg-orange-100 text-orange-600' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {enq.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right space-x-2">
                      {enq.status === 'unread' && (
                        <button 
                          onClick={() => markEnquiryRead(enq.id)}
                          className="text-green-600 hover:text-green-800 p-1 bg-green-50 rounded"
                          title="Mark as Read"
                        >
                          <FaCheck className="text-xs" />
                        </button>
                      )}
                      <button 
                        onClick={() => {
                          if (window.confirm('Delete this lead enquiry permanently?')) {
                            deleteEnquiry(enq.id);
                          }
                        }}
                        className="text-red-500 hover:text-red-700 p-1 bg-red-50 rounded"
                        title="Delete Enquiry"
                      >
                        <FaTrashAlt className="text-xs" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-16 text-slate-400 text-xs sm:text-sm">
              No customer lead enquiries received yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
